// Completion acceptance history — "what the user accepted floats up next
// time" (ranking level 2).
//
// SESSION-LOCAL BY DESIGN. CLAUDE.md routes per-(connection, database) USER
// METADATA into DatabaseMetaInfo (loose FKs, JSON/enum declarations …).
// This is not declared metadata but a usage signal with a short half-life:
// what you accepted ten minutes ago says what you are working on now, what
// you accepted last month mostly says which schema you had open. Persisting
// it would (a) write the metainfo JSON on every accept, (b) carry usage
// noise into MetaInfo export/import that teammates never asked for, and
// (c) age badly against schema changes with no invalidation story. So the
// store lives in renderer memory, scoped per (connectionId, database) so a
// different connection never sees another's habits, and starts empty on
// every app launch. Promote to DatabaseMetaInfo only if a persisted signal
// ever proves its worth — the ranker reads a plain map, so the storage can
// change without touching it.
//
// Engine-agnostic: recorded from Monaco's suggest-widget accept event and
// consumed by `rankCandidates` as an in-tier tie-breaker for all dialects.

export type AcceptanceRecord = {
  readonly count: number;
  /** Monotonic accept sequence — larger = more recent. */
  readonly lastAcceptedSeq: number;
};

export type AcceptanceScope = {
  readonly connectionId?: string;
  readonly database?: string;
};

/** Per-scope cap; the least recently accepted entry is evicted past it. */
export const ACCEPTANCE_HISTORY_CAP = 200;

const EMPTY: ReadonlyMap<string, AcceptanceRecord> = new Map();
const byScope = new Map<string, Map<string, AcceptanceRecord>>();
let seq = 0;

function scopeKey(scope: AcceptanceScope): string {
  return `${scope.connectionId ?? ''}|${scope.database ?? ''}`;
}

/** Normalise a completion label (or `filterText`) to the ranker's lookup key. */
export function acceptanceKey(label: string): string {
  const trimmed = label.trim();
  const bare =
    trimmed.length >= 2 && /^[[`"].*[\]`"]$/.test(trimmed) ? trimmed.slice(1, -1) : trimmed;
  return bare.toLowerCase();
}

export function recordAcceptedCompletion(scope: AcceptanceScope, label: string): void {
  const key = acceptanceKey(label);
  if (key.length === 0) return;
  const sk = scopeKey(scope);
  let entries = byScope.get(sk);
  if (!entries) {
    entries = new Map();
    byScope.set(sk, entries);
  }
  const prev = entries.get(key);
  seq += 1;
  entries.set(key, { count: (prev?.count ?? 0) + 1, lastAcceptedSeq: seq });
  if (entries.size > ACCEPTANCE_HISTORY_CAP) evictOldest(entries);
}

export function getAcceptanceHistory(scope: AcceptanceScope): ReadonlyMap<string, AcceptanceRecord> {
  return byScope.get(scopeKey(scope)) ?? EMPTY;
}

/** Clears every scope. Tests and the e2e coverage harness (per-script isolation). */
export function resetAcceptanceHistory(): void {
  byScope.clear();
  seq = 0;
}

function evictOldest(entries: Map<string, AcceptanceRecord>): void {
  let oldestKey: string | undefined;
  let oldestSeq = Number.POSITIVE_INFINITY;
  for (const [k, v] of entries) {
    if (v.lastAcceptedSeq < oldestSeq) {
      oldestSeq = v.lastAcceptedSeq;
      oldestKey = k;
    }
  }
  if (oldestKey !== undefined) entries.delete(oldestKey);
}

// E2E hook: the coverage harness accepts one suggestion per script, and a
// carried-over boost would make later scripts' ranking depend on earlier
// ones. Exposed unconditionally — a no-arg reset of an in-memory map is
// harmless outside tests.
if (typeof window !== 'undefined') {
  const w = window as unknown as {
    __jamSqlE2eResetAcceptanceHistory__?: () => void;
    __jamSqlE2eAcceptanceHistory__?: () => Record<string, string[]>;
  };
  w.__jamSqlE2eResetAcceptanceHistory__ = resetAcceptanceHistory;
  w.__jamSqlE2eAcceptanceHistory__ = () =>
    Object.fromEntries([...byScope].map(([scope, entries]) => [scope, [...entries.keys()]]));
}
